import { LOCKED_QUOTE_STATUSES, type QuoteStatus } from '../constants/index.ts';
import { bookingFrequency } from './booking.ts';

/**
 * The facts about a quote that decide whether its payments add up to a
 * status — camelCase so the web and the Worker's serialized quotes can both
 * pass themselves in.
 */
export interface QuotePaymentFacts {
  status: QuoteStatus | string;
  totalCents: number;
  acceptedFrequency: string | null;
  preferredFrequency: string | null;
  billingBasis: string | null;
  /**
   * `range` for a quote sent from a photo quote as a price range (spec
   * WILD-05). Absent or null for every other quote.
   */
  pricePromise?: string | null;
}

/**
 * Whether the quote sells one visit rather than a schedule — the same
 * question the booking asks (`bookingFrequency`), so a quote this calls
 * one-time is the quote the booking turns into a single visit.
 */
export function isOneTimeQuote(
  quote: Pick<QuotePaymentFacts, 'acceptedFrequency' | 'preferredFrequency' | 'billingBasis'>,
): boolean {
  return bookingFrequency(quote.acceptedFrequency, quote.preferredFrequency, quote.billingBasis) === 'one_time';
}

/**
 * Whether the payments on this quote add up to Paid, Partly paid or Unpaid,
 * and whether what is left counts as owed.
 *
 * Only an agreement the customer accepted (accepted, or completed after it)
 * owes anything, and only a one-time one owes its own total: a recurring or
 * monthly quote is paid visit by visit, so payments recorded on it are listed
 * but never measured against the quote's price. A quote with nothing to pay
 * has no status either.
 */
export function quoteTracksPayment(quote: QuotePaymentFacts): boolean {
  return (
    (LOCKED_QUOTE_STATUSES as readonly string[]).includes(quote.status) &&
    quote.totalCents > 0 &&
    isOneTimeQuote(quote) &&
    quoteHasPrice(quote)
  );
}

/**
 * Whether the quote names a price to pay. One sent from a photo quote as a
 * price range does not: its total is only the band's middle, and the final
 * price is set on its visits when they are completed, where they are paid.
 */
export function quoteHasPrice(quote: Pick<QuotePaymentFacts, 'pricePromise'>): boolean {
  return quote.pricePromise !== 'range';
}

/**
 * Whether a payment may be recorded on the quote now: an agreement the
 * customer accepted. A quote moved to another status afterwards keeps the
 * payments it already has, but takes no new ones until it is accepted again.
 */
export function quoteTakesPayments(status: QuoteStatus | string, pricePromise?: string | null): boolean {
  return (LOCKED_QUOTE_STATUSES as readonly string[]).includes(status) && quoteHasPrice({ pricePromise });
}
